import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import type { CwAttachment } from '@/api/inboxTypes';
import { formatDuration } from '@/lib/messageModel';

/** Одновременно играет одно голосовое: запуск нового останавливает прежнее. */
let playing: HTMLAudioElement | null = null;

/**
 * Голосовое: кнопка, полоса прогресса, длительность и расшифровка.
 * Расшифровку делает наш сервер (Unia::Voice): текст — в transcribed_text, состояние — в
 * unia_transcript.state (pending → done | failed). Пока pending, показываем «расшифровывается».
 */
export function AudioPlayer({ att }: { att: CwAttachment }) {
  const ref = useRef<HTMLAudioElement>(null);
  const [on, setOn] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => () => { if (playing === ref.current) playing = null; }, []);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) {
      if (playing && playing !== a) playing.pause();
      playing = a;
      void a.play().catch(() => setFailed(true));
    } else {
      a.pause();
    }
  };

  const text = att.transcribed_text?.trim();
  const state = att.unia_transcript?.state;
  return (
    <>
      <div className="voice">
        <button type="button" className="pl" onClick={toggle} aria-label={on ? 'Пауза' : 'Прослушать голосовое'}>
          {on ? <Pause aria-hidden /> : <Play aria-hidden />}
        </button>
        <span className="track" aria-hidden><i style={{ width: dur ? `${Math.min(100, (pos / dur) * 100)}%` : '0%' }} /></span>
        <span className="tm tnum">{formatDuration(on || pos > 0 ? pos : dur)}</span>
        <audio
          ref={ref}
          src={att.data_url}
          preload="none"
          onPlay={() => setOn(true)}
          onPause={() => setOn(false)}
          onEnded={() => { setOn(false); setPos(0); }}
          onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setDur(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
          onError={() => setFailed(true)}
        />
      </div>
      {failed && <div className="tr">Запись не воспроизводится</div>}
      {text
        ? <div className="tr">{text}</div>
        : state === 'pending' ? <div className="tr">Расшифровывается…</div>
          : state === 'failed' ? <div className="tr">Расшифровать не удалось</div>
            : null}
    </>
  );
}
